import { getAudioSettings, mixedVolume, subscribeAudioSettings } from "./audioSettings";

export const MENU_BGM_KEY = "menu-select-your-champion";
export const MENU_BGM_URL = "/assets/audio/menu-select-your-champion.mp3?v=2";
export const MENU_BGM_VOLUME = 0.42;
export const MENU_BGM_FADE_MS = 480;

const STOP_MENU_BGM = "__breakroomStopMenuBgm";

export function shouldPlayMenuTheme(menuVisible: boolean, menuThemeEnabled = getAudioSettings().menuTheme): boolean {
  return menuVisible && menuThemeEnabled;
}

export function pageAllowsMenuTheme(pageVisible = !document.hidden, pageFocused = document.hasFocus()): boolean {
  return pageVisible && pageFocused;
}

export function destroyMenuBgmElements(): void {
  const stopper = (globalThis as typeof globalThis & { [STOP_MENU_BGM]?: () => void })[STOP_MENU_BGM];
  stopper?.();
  for (const leftover of document.querySelectorAll<HTMLAudioElement>("audio[data-menu-bgm]")) {
    leftover.pause();
    leftover.removeAttribute("src");
    leftover.load();
    leftover.remove();
  }
}

export function createMenuBgm(
  isMenuVisible: () => boolean,
  audio: HTMLAudioElement = new Audio(MENU_BGM_URL)
): {
  sync(): void;
  stop(immediate?: boolean): void;
} {
  destroyMenuBgmElements();

  audio.loop = true;
  audio.preload = "auto";
  audio.hidden = true;
  audio.autoplay = false;
  audio.dataset.menuBgm = MENU_BGM_KEY;
  if (!audio.isConnected) document.body.append(audio);

  let fadeTimer = 0;
  let disposed = false;
  let unlockBound = false;

  const targetVolume = (): number => mixedVolume("music", MENU_BGM_VOLUME);

  const applyVolume = (): void => {
    if (fadeTimer) return;
    audio.volume = targetVolume();
  };

  const clearFade = (): void => {
    if (fadeTimer) window.clearInterval(fadeTimer);
    fadeTimer = 0;
  };

  const stop = (immediate = true): void => {
    clearFade();
    audio.pause();
    audio.currentTime = 0;
    applyVolume();
  };

  const start = async (): Promise<void> => {
    if (disposed) return;
    clearFade();
    applyVolume();
    if (targetVolume() <= 0) return;
    if (!audio.paused) return;
    try {
      await audio.play();
    } catch {
      bindAutoplayUnlock();
    }
  };

  const bindAutoplayUnlock = (): void => {
    if (unlockBound || disposed) return;
    unlockBound = true;
    const unlock = (): void => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
      unlockBound = false;
      sync();
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
  };

  const sync = (): void => {
    if (disposed) return;
    if (!pageAllowsMenuTheme() || !shouldPlayMenuTheme(isMenuVisible())) {
      stop(true);
      return;
    }
    void start();
  };

  applyVolume();
  const unsubscribe = subscribeAudioSettings(sync);
  document.addEventListener("visibilitychange", sync);
  window.addEventListener("focus", sync);
  window.addEventListener("blur", sync);
  sync();

  const dispose = (): void => {
    disposed = true;
    unsubscribe();
    document.removeEventListener("visibilitychange", sync);
    window.removeEventListener("focus", sync);
    window.removeEventListener("blur", sync);
    stop(true);
    audio.removeAttribute("src");
    audio.load();
    audio.remove();
  };

  (globalThis as typeof globalThis & { [STOP_MENU_BGM]: () => void })[STOP_MENU_BGM] = dispose;

  const hot = (import.meta as ImportMeta & { hot?: { dispose: (callback: () => void) => void } }).hot;
  hot?.dispose(dispose);

  return { sync, stop };
}
